import React, { useState } from 'react';
import Flatpickr from 'react-flatpickr';
import Select from "react-select";
import useReactSelectCustomStyle from '../../hooks/useReactSelectCustomStyle';
import moment from 'moment';
import { addCertificateAPI } from '../../apiConfig/apiCall';
import { toast } from 'react-toastify';
import { Button, Col, Form, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { getCertificationList } from '../../slices/ManageCompliance/thunk';

export default function AddCertificateModal({
    addCertificateModal,
    setAddCertificateModal,
    toggleAddCertificateModal
}) {
    const dispatch = useDispatch();
    const selectBoxCustomStyles = useReactSelectCustomStyle();

    const [submittingForm, setSubmittingForm] = useState(false); // submittingForm state

    const statusOptions = [
        { value: 'ACTIVE', label: 'ACTIVE' },
        { value: 'INACTIVE', label: 'INACTIVE' },
    ];

    const initialFormData = {
        certificationVersion: "",
        status: null,
        launchDate: moment().format('YYYY-MM-DD'),
    };

    const [formData, setFormData] = useState(initialFormData);

    const handleChange = (field, value) => {
        const maxLength = 20;
        if (value.length <= maxLength) {
            setFormData((prevState) => ({
                ...prevState,
                [field]: value,
            }));
        } else {
            toast.error(`Maximum character limit reached for certificationVersion (${maxLength})`);
        }
    };
    

    const resetFormFields = () => {
        setFormData(initialFormData);
    };

    const handleSubmitAddCertificate = async (e) => {
        e.preventDefault();
        if (submittingForm) return;
        try {
            const { certificationVersion, status } = formData;
            if (!certificationVersion) {
                toast.error("Certificate Version is required");
                return;
            } else if (certificationVersion.length > 20) {
                toast.error("Certificate Version must be a maximum of 20 characters");
                return;
            } else if (status === null) {
                toast.error("Status is required");
                return;
            }

            const formattedLaunchDate = moment(formData.launchDate[0]).format('YYYY-MM-DD');
            const dataToSubmit = {
                certificationVersion: formData.certificationVersion,
                overView: "",
                status: formData.status,
                launchDate: formattedLaunchDate,
            };

            setSubmittingForm(true);
            const apiResponse = await addCertificateAPI(dataToSubmit);
            if (apiResponse.status === 200) {
                toast.success(apiResponse.data);
                resetFormFields(); // Reset fields after successful submission
                setAddCertificateModal(false);
                dispatch(getCertificationList());
            } else if (apiResponse.status === 400) {
                toast.error(apiResponse.data);
            } else {
                toast.error("Something went wrong");
            }
        } catch (error) {
            console.error('Error sending data:', error);
            toast.error('An error occurred while adding the certificate');
        } finally {
            setSubmittingForm(false);
        }
    };

    return (
        <>
            <Modal isOpen={addCertificateModal} toggle={toggleAddCertificateModal}>
                <ModalHeader toggle={toggleAddCertificateModal}>Add Certificate</ModalHeader>
                <ModalBody>
                    <Form>
                        <Row className="gy-3">
                            <Col lg={12}>
                                <div>
                                    <Label htmlFor="certificationVersion" className="form-label">Certificate Version</Label>
                                    <Input
                                        type="text"
                                        className="form-control"
                                        id="certificationVersion"
                                        value={formData.certificationVersion}
                                        onChange={(e) => handleChange('certificationVersion', e.target.value)}
                                    />
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div>
                                    <Label htmlFor="status" className="form-label">Status</Label>
                                    <Select
                                        value={formData.status ? statusOptions.find(option => option.value === formData.status) : null}
                                        onChange={(option) => handleChange('status', option?.value || null)}
                                        options={statusOptions}
                                        styles={selectBoxCustomStyles}
                                    />
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div>
                                    <Label htmlFor="launchDate" className="form-label">Launch Date</Label>
                                    <Flatpickr
                                        className="form-control"
                                        options={{ dateFormat: "Y-m-d" }}
                                        value={formData.launchDate}
                                        onChange={(date) => handleChange('launchDate', date)}
                                    />
                                </div>
                            </Col>
                            <Col lg={12}>
                                {submittingForm ? (
                                    <Button color="primary" className="me-2" type="button" disabled>
                                        <Spinner size="sm"> Loading... </Spinner>
                                    </Button>
                                ) : (
                                    <Button
                                        color="primary"
                                        type="submit"
                                        className="me-2"
                                        disabled={submittingForm}
                                        onClick={handleSubmitAddCertificate}
                                    >
                                        Add
                                    </Button>
                                )}
                                <Button
                                    color="secondary"
                                    type="reset"
                                    disabled={submittingForm}
                                    onClick={resetFormFields}
                                >
                                    Reset
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
            </Modal>
        </>
    );
}